import { Check } from 'lucide-react';
import { useEffect, useRef, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import type { StageBarKey, StageBarStage } from '@nest-recruit/shared';
import { formatNumber } from '../lib/format';
import styles from './StageBar.module.css';

// JOB-12's stage bar (§L33, QoL §15, `Design System/project/guidelines/stage-bar.card.html`):
// numbered pills joined by a hairline, done · current · upcoming in the ADR 0035 status
// tones, and "Out: N" at the end. Each stage is a real link, so it opens in a new tab
// (06-qol §8); a plain click swaps the content below without loading a page (§5a rule 5).

interface StageBarProps {
  readonly stages: readonly StageBarStage[];
  readonly setupMissing: number;
  readonly outTotal: number;
  /** The stage on screen, or null when the page shows something from the ⋯ menu. */
  readonly activeKey: StageBarKey | null;
  readonly hrefOf: (key: StageBarKey) => string;
  readonly onSelect: (key: StageBarKey) => void;
  readonly onOut: () => void;
}

export function StageBar({ stages, setupMissing, outTotal, activeKey, hrefOf, onSelect, onOut }: StageBarProps): ReactNode {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const focusKey = activeKey ?? stages.find((stage) => stage.state === 'current')?.key ?? stages[0]?.key;

  // QoL §15: on a phone the bar scrolls sideways, and the stage on screen stays in view.
  useEffect(() => {
    const active = trackRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    active?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [activeKey]);

  function onClick(event: MouseEvent<HTMLAnchorElement>, key: StageBarKey): void {
    // A modified click is the browser's: a new tab or window, as on any link.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    onSelect(key);
  }

  // 06-qol §2: arrow keys move along the bar, Home and End jump to its ends.
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const index = stages.findIndex((stage) => stage.key === focusKey);
    const next = nextIndex(event.key, index, stages.length);
    if (next === null) return;
    event.preventDefault();
    const stage = stages[next];
    if (!stage) return;
    onSelect(stage.key);
    trackRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  }

  return (
    <div className={styles.bar}>
      <div ref={trackRef} className={styles.track} role="tablist" aria-label="Stages" onKeyDown={onKeyDown}>
        {stages.map((stage, index) => (
          <StagePill
            key={stage.key}
            stage={stage}
            setupMissing={setupMissing}
            isActive={stage.key === activeKey}
            isFocusable={stage.key === focusKey}
            isLinkPassed={index > 0 && stage.state !== 'upcoming'}
            isFirst={index === 0}
            href={hrefOf(stage.key)}
            onClick={(event) => onClick(event, stage.key)}
          />
        ))}
      </div>
      <button type="button" className={styles.out} onClick={onOut} aria-haspopup="dialog">
        Out: <span className={styles.outCount}>{formatNumber(outTotal)}</span>
      </button>
    </div>
  );
}

interface StagePillProps {
  readonly stage: StageBarStage;
  readonly setupMissing: number;
  readonly isActive: boolean;
  readonly isFocusable: boolean;
  readonly isLinkPassed: boolean;
  readonly isFirst: boolean;
  readonly href: string;
  readonly onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
}

function StagePill({ stage, setupMissing, isActive, isFocusable, isLinkPassed, isFirst, href, onClick }: StagePillProps): ReactNode {
  return (
    <>
      {!isFirst && <span className={styles.link} data-passed={isLinkPassed} aria-hidden />}
      <a
        href={href}
        role="tab"
        aria-selected={isActive}
        tabIndex={isFocusable ? 0 : -1}
        className={styles.stage}
        data-state={stage.state}
        onClick={onClick}
      >
        <span className={styles.number} aria-hidden>
          {stage.number === null ? <Check size={12} strokeWidth={3} /> : stage.number}
        </span>
        <span className={styles.label}>{stage.label}</span>
        <StageCount stage={stage} setupMissing={setupMissing} />
        {stage.detail && <span className={styles.detail}>{stage.detail}</span>}
        {stage.state === 'done' && <Check className={styles.tick} size={14} strokeWidth={3} aria-label="done" />}
      </a>
    </>
  );
}

/** §L33: ① Job shows readiness, every other stage its live count. */
function StageCount({ stage, setupMissing }: { readonly stage: StageBarStage; readonly setupMissing: number }): ReactNode {
  if (stage.count !== null) return <span className={styles.count}>{formatNumber(stage.count)}</span>;
  return <span className={styles.readiness}>{setupMissing === 0 ? 'Ready' : `${formatNumber(setupMissing)} missing`}</span>;
}

function nextIndex(key: string, index: number, length: number): number | null {
  if (length === 0) return null;
  if (key === 'ArrowRight') return Math.min(index + 1, length - 1);
  if (key === 'ArrowLeft') return Math.max(index - 1, 0);
  if (key === 'Home') return 0;
  if (key === 'End') return length - 1;
  return null;
}
